import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";

const EMPTY = { label: "", title: "", service_type: "" };

function PresetForm({ value, onChange, types, idPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div className="space-y-1">
        <Label className="text-xs">Category name</Label>
        <Input data-testid={`${idPrefix}-label`} value={value.label} placeholder="e.g. Prayer Night"
          onChange={(e) => onChange({ ...value, label: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Session name</Label>
        <Input data-testid={`${idPrefix}-title`} value={value.title} placeholder="Same as category"
          onChange={(e) => onChange({ ...value, title: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Gathering type</Label>
        <Input data-testid={`${idPrefix}-type`} value={value.service_type} list="gathering-types" placeholder="Pick or type"
          onChange={(e) => onChange({ ...value, service_type: e.target.value })} />
        <datalist id="gathering-types">{types.map((t) => <option key={t} value={t} />)}</datalist>
      </div>
    </div>
  );
}

export const SessionPresetsDialog = ({ open, onOpenChange, presets, types, onChanged }) => {
  const [adding, setAdding] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState(EMPTY);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); return false; }
  };
  const add = async () => { if (await run(() => api.post("/session-presets", adding), "Category added")) setAdding(EMPTY); };
  const save = async () => { if (await run(() => api.put(`/session-presets/${editId}`, editVal), "Category updated")) setEditId(null); };
  const remove = (p) => { if (window.confirm(`Remove the "${p.label}" category? Its past sessions are kept.`)) run(() => api.delete(`/session-presets/${p.id}`), "Category removed"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="session-presets-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Attendance Categories</DialogTitle>
          <DialogDescription>Each category is a button on the Attendance tab. Sessions are grouped by their gathering type, and New Session pre-fills the session name and type.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-xl">
          {presets.map((p) => (
            <li key={p.id} className="p-3" data-testid={`preset-row-${p.id}`}>
              {editId === p.id ? (
                <div className="space-y-2">
                  <PresetForm value={editVal} onChange={setEditVal} types={types} idPrefix="preset-edit" />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="h-4 w-4 mr-1" />Cancel</Button>
                    <Button size="sm" onClick={save} data-testid="preset-edit-save" className="bg-[#2D5A27] hover:bg-[#23471F] text-white"><Check className="h-4 w-4 mr-1" />Save</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1C241B]">{p.label}</p>
                    <p className="text-xs text-[#7A8A78]">{p.title} · {p.service_type}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[#526050]" data-testid={`preset-edit-${p.id}`}
                    onClick={() => { setEditId(p.id); setEditVal({ label: p.label, title: p.title, service_type: p.service_type }); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`preset-delete-${p.id}`} onClick={() => remove(p)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
          {presets.length === 0 && <li className="p-6 text-center text-sm text-[#7A8A78]">No categories yet.</li>}
        </ul>
        <div className="mt-2 space-y-2 rounded-xl bg-[#F7F5F0] p-4">
          <p className="text-sm font-semibold text-[#1C241B]">Add a category</p>
          <PresetForm value={adding} onChange={setAdding} types={types} idPrefix="preset-new" />
          <div className="flex justify-end">
            <Button onClick={add} data-testid="preset-add-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
